import {
    Workflow,
    WorkflowsService,
    AccountReceivableDTO
    } from '@/api';
    import { Button } from '@/components/ui/button';
    import { cn } from '@/lib/utils';
    import { useStore } from '@nanostores/react';
    import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
    import {
      ArrowLeft,
      FileText,
      FileUp,
      FolderIcon,
      Loader2,
      X,
    } from 'lucide-react';
    import { toast } from 'sonner';
    import { useState, useEffect } from 'react';
    import { Checkbox } from '@/components/ui/checkbox';
  
    interface Props {
      open: boolean;
      setOpen: React.Dispatch<React.SetStateAction<boolean>>;
      setNodes: any;
      selectedNode: any;
      workFlowId: string;
    }
  
  
      
    const fetchAccountReceivables = async (workflow: Workflow) => {
      const pausedAccountReceivables = await WorkflowsService.getUserPausedWorkflowExecutions(workflow.id);
      const timerPausedAccountReceivables = await WorkflowsService.getTimerPausedWorkflowExecutions(workflow.id);
      
      const pausedAccounts = pausedAccountReceivables.accountReceivables ?? [];
      const timerPausedAccounts = timerPausedAccountReceivables.accountReceivables ?? [];
      return [...pausedAccounts, ...timerPausedAccounts];
    };
    
    export default function WorkflowSkipDrawer({
      open,
      setOpen,
      setNodes,
      selectedNode,
      workFlowId,
    }: Props) {
      const org = useStore($org);
      const workflow = useStore($workflow);
      const [accounts, setAccounts] = useState<AccountReceivableDTO[]>([]);
      const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  
      useEffect(() => {
        if (workflow) {
          fetchAccountReceivables(workflow).then(setAccounts);
        }
      }, [workflow]);
  
      const handleSelectAll = () => {
        if (selectedAccountIds.length === accounts.length) {
          setSelectedAccountIds([]); // Deselect all
        } else {
          setSelectedAccountIds(accounts.map(a => a.id)); // Select all
        }
      };
  
      const handleAccountSelect = (accountId: string) => {
        setSelectedAccountIds(prev => 
          prev.includes(accountId)
            ? prev.filter(id => id !== accountId) // Remove if selected
            : [...prev, accountId] // Add if not selected
        );
      };
  
      const handleRun = async () => {
        if (selectedAccountIds.length === 0) {
          toast.error("Please select at least one account");
          return;
        }
  
        try {
          toast.success("Skipping workflow execution(s) node...");
          setOpen(false); // Close drawer after successful execution

          await WorkflowsService.manualSkipNodeBulkWorkflowExecution(workFlowId, {
            accountReceivableIds: selectedAccountIds
          });
  
        } catch (error) {
          toast.error("Failed to skip node");
          console.error(error);
        }
      };
  
      return (
        <div className={`absolute right-6 top-20 min-h-[160px] min-w-[360px] rounded-lg bg-white p-4 pl-6 pr-6 ${!open && 'hidden'} z-10`}>
          <div className="flex justify-between">
            <div className="flex items-center">
              <p className="text-sm font-semibold tracking-tight">
                Workflow Skip Timer Node
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
  
          {accounts.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-3">Accounts</h3>
              
              <div className="flex items-center mb-2">
                <Checkbox
                  checked={selectedAccountIds.length === accounts.length}
                  onCheckedChange={handleSelectAll}
                />
                <label className="ml-2 text-sm font-medium">
                  Select All ({accounts.length})
                </label>
              </div>
  
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {accounts.map(account => (
                <div key={account.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    checked={selectedAccountIds.includes(account.id)}
                    onCheckedChange={() => handleAccountSelect(account.id)}
                  />
                  <div className="ml-2 text-sm font-medium">
                    {account.customer?.contactEmail}
                  </div>
                </div>
                <Button variant="secondary" size="sm" className="h-6">
                  {account.status}
                </Button>
              </div>
                ))}
              </div>
  
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleRun}
                  disabled={selectedAccountIds.length === 0}
                >
                  Skip
                </Button>
              </div>
            </div>
          )}
  
          {accounts.length === 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              No accounts found for this workflow to skip timer node on.
            </div>
          )}
        </div>
      );
    }
    